import { useCallback } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../store/store";
import { CareTriadKey } from "../organisms/PatientDetailsV2/CoachAssessment/AssessmentProgressContext";

const TRIAD_VIEWS: Record<CareTriadKey, string[]> = {
  diet: [
    "diet",
    "diet-baseline",
    "diet-plans",
    "diet-adherence",
    "diet-reports",
  ],
  exercise: ["exercise", "exercise-baseline", "exercise-plan"],
  success: ["success", "success-baseline"],
};

const ALL_TRIAD_VIEWS = Object.values(TRIAD_VIEWS).flat();

const TRIAD_ROLE_PREFIXES: Record<CareTriadKey, string[]> = {
  diet: ["nutritionist"],
  exercise: ["physiotherapist"],
  success: ["psychologist", "psychoanalyst", "success coach"],
};

const CHIEF_HC_YES = "Y";

/**
 * Care Triad visibility plus diet/exercise plan edit rights.
 * Every coach may open every Care Triad view (read).
 * Diet/exercise plan edits stay on canManageDiet / canManageExercise.
 * Assessment edits stay on canEditAssessment (chief edits all).
 */
const useCoachPermissions = () => {
  const { healthCoachDetails } = useSelector(
    (state: RootState) => state.healthCoach,
  );
  const coach = healthCoachDetails?.data;
  const role = (coach?.role || "").toLowerCase();
  const isChief = coach?.chief_hc === CHIEF_HC_YES;

  const canManage = (discipline: string) =>
    isChief || !!role.startsWith(discipline.toLowerCase());

  const matchesTriadRole = useCallback(
    (key: CareTriadKey) =>
      TRIAD_ROLE_PREFIXES[key].some(
        (prefix) => role.startsWith(prefix) || role.includes(prefix),
      ),
    [role],
  );

  const canSeeCareTriad = useCallback((_key?: CareTriadKey) => true, []);

  const canManageCareTriad = useCallback(
    (key: CareTriadKey) => isChief || matchesTriadRole(key),
    [isChief, matchesTriadRole],
  );

  const canEditAssessment = useCallback(
    (key: CareTriadKey) => isChief || matchesTriadRole(key),
    [isChief, matchesTriadRole],
  );

  const isCareTriadViewAllowed = useCallback((view: string) => {
    if (!ALL_TRIAD_VIEWS.includes(view)) return true;
    return true;
  }, []);

  return {
    canManageDiet: canManage("Nutritionist"),
    canManageExercise: canManage("Physiotherapist"),
    canSeeCareTriad,
    canManageCareTriad,
    canEditAssessment,
    isCareTriadViewAllowed,
    isChief,
  };
};

export default useCoachPermissions;
